/**
 * TODO:
 * - desktop styling (higher rows, ...)
 * - auto scroll
 */

import './ScheduleTable.css'

import { compose, withProps } from 'recompose'
import React, { Component } from 'react'
import { withRouter } from 'react-router-dom'
import muiThemeable from 'material-ui/styles/muiThemeable'
import LocIcon from 'material-ui/svg-icons/communication/location-on'
import _ from 'lodash'

import {
  byDatesAsc,
  byTimesAsc,
  currentDate,
  formatLocalDateString,
  getDayName,
  timeToDate,
  toLocalDate,
  toLocalDateString,
  toLocalTimeString,
} from '../app/timeUtils'
import { getPreferredLocales, getSessions } from '../app/selectors'
import TimeIcon from '../assets/svg/TimeIcon'
import InlineIconWrapper from '../components/InlineIconWrapper'
import { withDialogs } from '../components/DialogProvider'
import { withSizes } from '../app/utils'
import DayCaption from '../components/DayCaption'
import content from '../content'
import { presentationDetailRoute } from '../app/routes'
import { isTrackCrossTrack, isSessionCrossTrack } from '../app/predicates'

const MOBILE_MARGIN = 6
const DESKTOP_MARGIN = 13

class _DayScheduleTable extends Component {
  cardRefs = {}

  render() {
    const {
      schedule,
      preferredLocales,
      tracks,
      trackIndices,
      isMobile,
      muiTheme,
    } = this.props
    const { custom: { metrics: { scheduleTimeRatio } } } = muiTheme
    const dateString = formatLocalDateString(schedule.date, preferredLocales)
    const dayName = getDayName(toLocalDate(schedule.date), preferredLocales)
    const startTime = schedule.sessions[0].time
    const endTime = schedule.sessions.slice(-1)[0].endTime
    const timeDiffFromStartInMinutes = time => {
      const start = timeToDate(startTime)
      const end = timeToDate(time)
      return Math.round((end.getTime() - start.getTime()) / 60000)
    }
    const trackWidth = getTrackWidth(tracks.length)
    return (
      <div className="ScheduleTable-dayContainer">
        <DayCaption dateString={dateString} dayName={dayName} />
        <div className="ScheduleTable-timeGrid">
          <div className="ScheduleTable-cards">
            {schedule.sessions.map(session => {
              const startDiff = timeDiffFromStartInMinutes(session.time)
              const endDiff = timeDiffFromStartInMinutes(session.endTime)
              const duration = endDiff - startDiff
              const trackIndex = trackIndices[session.track.id]
              const width = isSessionCrossTrack(session)
                ? '100%'
                : trackWidth + '%'
              const height = duration * scheduleTimeRatio
              const backgroundColor = session.track.color
              const wrappingMode = height < 42 ? 'one-line' : 'two-line'
              const cardMargin = isMobile ? MOBILE_MARGIN : DESKTOP_MARGIN
              return (
                <div
                  key={session.id}
                  className="ScheduleTable-cardContainer"
                  style={{
                    width,
                    height,
                    top: startDiff * scheduleTimeRatio,
                    left: trackIndex * trackWidth + '%',
                    padding: `2px ${cardMargin}px 1px ${cardMargin}px`,
                  }}
                >
                  <div
                    onClick={this.onSessionClick(session)}
                    ref={this.onRef(session)}
                    className={`ScheduleTable-card ${wrappingMode}`}
                    style={{ backgroundColor }}
                  >
                    <div className="ScheduleTable-cardTime">
                      {session.time} - {session.endTime}
                    </div>
                    <div className="ScheduleTable-cardPresentationName">
                      {_.get(session, 'presentation.title') ||
                        _.get(session, 'meta.title')}
                    </div>
                    <div className="ScheduleTable-cardPresenter">
                      {_.get(session, 'presentation.presenter.fullName')}
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
          <TimeGrid
            tracks={tracks}
            startTime={startTime}
            endTime={endTime}
            scheduleTimeRatio={scheduleTimeRatio}
          />
        </div>
      </div>
    )
  }

  onRef = row => ref => {
    this.cardRefs[row.time] = { row, ref }
  }

  onSessionClick = session => {
    const { history, openDialog } = this.props
    return session.presentation
      ? () => history.push(presentationDetailRoute(session.presentation.id))
      : () => openDialog(makeMetaDialogProps(session))
  }

  scrollToTime(time) {
    const wrapper = this.cardRefs[time]
    if (!wrapper) {
      return
    }
    const { appBar: { height: appBarHeight } } = this.props.muiTheme
    const scrollPadding = 20
    window.scrollTo(0, wrapper.ref.offsetTop - appBarHeight - scrollPadding)
  }

  componentDidMount() {
    this.props.customRef(this)
  }
}

/**
Returns (number) percentage of width for given total count of tracks
*/
function getTrackWidth(count) {
  return 100 / (count || 1)
}

function TimeGrid({ startTime, endTime, tracks, scheduleTimeRatio }) {
  const timeRange = [
    parseInt(startTime, 10),
    parseInt(endTime, 10) + (endTime.endsWith(':00') ? 0 : 1),
  ]
  return (
    <div>
      {_.range(timeRange[0], timeRange[1] + 1).map(hour => (
        <div
          key={hour}
          className={`ScheduleTable-hourRow ${
            hour === timeRange[1] ? 'last' : ''
          }`}
        >
          <div className="ScheduleTable-hourLabelColumn">
            {toLocalTimeString(timeToDate(`${hour}:00`))}
          </div>
          <div
            className="ScheduleTable-hourLinesColumn"
            style={{
              height: scheduleTimeRatio * 60,
            }}
          />
        </div>
      ))}
      <div className="ScheduleTable-trackLabelsContainer">
        {tracks.map(track => (
          <div
            key={track.label}
            className="ScheduleTable-trackName"
            style={{ width: getTrackWidth(tracks.length) + '%' }}
          >
            {track.label}
          </div>
        ))}
      </div>
    </div>
  )
}

export function makeMetaDialogProps(session) {
  if (!session || !session.meta) {
    return {}
  }
  const endTime = session.endTime ? ` - ${session.endTime}` : ''
  const time = `${session.time}${endTime}`
  return {
    title: session.meta.title,
    children: (
      <React.Fragment>
        <div>
          <InlineIconWrapper iconComponent={LocIcon}>
            {session.track.label}
          </InlineIconWrapper>
        </div>
        <div>
          <InlineIconWrapper iconComponent={TimeIcon}>{time}</InlineIconWrapper>
        </div>
        {session.meta.description && (
          <div style={{ marginTop: 20 }}>{session.meta.description}</div>
        )}
      </React.Fragment>
    ),
  }
}

const withPageWidth = withSizes(({ width }) => ({ width }))
const DayScheduleTable = compose(
  withDialogs,
  withPageWidth,
  withRouter,
  muiThemeable()
)(_DayScheduleTable)

class ScheduleTable extends Component {
  scheduleRefs = {}

  render() {
    const {
      tracks,
      trackIndices,
      sessionsByDate,
      preferredLocales,
    } = this.props
    return (
      <div className="ProgramPage-programContainer">
        {sessionsByDate.map(schedule => (
          <div key={schedule.date}>
            <DayScheduleTable
              customRef={this.onRef(schedule)}
              schedule={schedule}
              tracks={tracks}
              trackIndices={trackIndices}
              preferredLocales={preferredLocales}
            />
          </div>
        ))}
      </div>
    )
  }

  onRef = schedule => ref => {
    this.scheduleRefs[schedule.date] = { schedule, ref }
  }

  scrollToScheduleTime() {
    if (this.props.history.action === 'POP') {
      // don't handle back button
      return
    }
    if ('scrollRestoration' in window.history) {
      // enable manual scroll restoration for the moment of custom scrolling
      window.history.scrollRestoration = 'manual'
      setTimeout(() => {
        window.history.scrollRestoration = 'auto'
      }, 500)
    }
    const now = currentDate()
    const current = toLocalDateString(now)
    const wrapper = this.scheduleRefs[current]
    if (wrapper) {
      const currentTime = toLocalTimeString(now)
      const times = wrapper.schedule.sessions
        .map(r => r.time)
        .concat([currentTime])
      times.sort(byTimesAsc)
      const timeIndex = times.lastIndexOf(currentTime) - 1
      if (timeIndex >= 0) {
        const closestTime = times[timeIndex]
        wrapper.ref.scrollToTime(closestTime)
        return
      }
    }
    // scroll to top otherwise
    window.scrollTo(0, 0)
  }

  componentDidMount() {
    this.scrollToScheduleTime()
  }

  componentDidUpdate(prevProps) {
    if (this.props.location !== prevProps.location) {
      this.scrollToScheduleTime()
    }
  }
}

function groupSessionsByDates(sessions) {
  const byDayPairs = _.toPairs(_.groupBy(sessions, 'date'))
  byDayPairs.sort(([date1], [date2]) => byDatesAsc(date1, date2))
  return byDayPairs.map(([date, sessions]) => {
    sessions.sort((session1, session2) =>
      byTimesAsc(session1.time, session2.time)
    )
    return { date, sessions }
  })
}

const withPageContent = withProps(ownProps => {
  const sessions = getSessions(content)
  const sessionsByDate = groupSessionsByDates(sessions)
  const tracks = []
  const trackIndices = {}
  sessionsByDate.forEach(({ sessions }) =>
    sessions.forEach(({ track }) => {
      if (!track || isTrackCrossTrack(track)) {
        return
      }
      if (typeof trackIndices[track.id] === 'undefined') {
        tracks.push(track)
        trackIndices[track.id] = tracks.length - 1
      }
    })
  )

  return {
    tracks,
    trackIndices,
    sessionsByDate,
    preferredLocales: getPreferredLocales(content),
  }
})

export default compose(withPageContent, withRouter)(ScheduleTable)



// WEBPACK FOOTER //
// ./src/containers/ScheduleTable.js